import * as React from "react";
import { ConfirmDialog } from "./ConfirmDialog";
import { REFINED } from "../risikoanalyseStep/tokens";
import { I } from "./kvpIcons";

/**
 * Popup für EINEN Namen — Dokument umbenennen, Ordner anlegen/umbenennen
 * (User 03.11.2026: „nach unseren Guidelines“, statt Fluent-Dialog).
 *
 * Dieselbe Hülle wie {@link ConfirmDialog} (Aubergine-Badge, Gold-Linie, Fuß
 * mit Abbrechen/Bestätigen), im Inhalt ein Eingabefeld:
 *  - `endung` steht fest daneben — sie bestimmt Vorschau und Download und ist
 *    nicht änderbar (das Backend erzwingt sie ohnehin);
 *  - `ort` sagt, wo das liegt („Ordner: Verträge/2019“);
 *  - `hinweis` erscheint LIVE beim Tippen (z. B. „/“ wird ersetzt), `fehler`
 *    nach einem gescheiterten Speichern.
 *
 * Enter speichert, Escape und ein Klick daneben brechen ab. Beim Öffnen ist der
 * Name markiert: wer umbenennt, will ihn meist ganz ersetzen.
 */
export interface NamenEingabeDialogProps {
    titel: string;
    wert: string;
    onWert: (wert: string) => void;
    onSpeichern: () => void;
    onAbbrechen: () => void;
    endung?: string;
    ort?: React.ReactNode;
    hinweis?: React.ReactNode;
    fehler?: string | null;
    busy?: boolean;
    bestaetigenText?: string;
    feldName?: string;
    platzhalter?: string;
    icon?: React.ReactNode;
}

export const NamenEingabeDialog: React.FC<NamenEingabeDialogProps> = ({
    titel,
    wert,
    onWert,
    onSpeichern,
    onAbbrechen,
    endung,
    ort,
    hinweis,
    fehler,
    busy = false,
    bestaetigenText = "Umbenennen",
    feldName = "Name",
    platzhalter,
    icon,
}) => {
    const feldRef = React.useRef<HTMLInputElement | null>(null);
    const feldId = React.useId();
    const [fokus, setFokus] = React.useState(false);

    React.useEffect(() => {
        const t = window.setTimeout(() => {
            feldRef.current?.focus();
            feldRef.current?.select();
        }, 0);
        return () => window.clearTimeout(t);
    }, []);

    const rahmen = fehler ? REFINED.err : fokus ? REFINED.aub : REFINED.line;

    return (
        <ConfirmDialog
            open
            title={titel}
            tone="primary"
            icon={icon ?? I.edit}
            confirmLabel={bestaetigenText}
            busy={busy}
            focusConfirm={false}
            // Ein leerer Name geht durch: der Aufrufer meldet ihn mit seinem Satz.
            onConfirm={onSpeichern}
            onCancel={onAbbrechen}
            message={
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <label
                        style={{
                            fontSize: REFINED.fs.xs,
                            fontWeight: 700,
                            color: REFINED.ink2,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                        }}
                        htmlFor={feldId}
                    >
                        {feldName}
                    </label>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "stretch",
                            border: `1px solid ${rahmen}`,
                            borderRadius: 10,
                            background: busy ? REFINED.fieldBg : REFINED.white,
                            boxShadow: fehler ? REFINED.errRing : fokus ? REFINED.focusRing : "none",
                            overflow: "hidden",
                            transition: "border-color 120ms, box-shadow 120ms",
                        }}
                    >
                        <input
                            id={feldId}
                            ref={feldRef}
                            value={wert}
                            disabled={busy}
                            placeholder={platzhalter}
                            aria-invalid={fehler ? true : undefined}
                            spellCheck={false}
                            onFocus={() => setFokus(true)}
                            onBlur={() => setFokus(false)}
                            onChange={(e) => onWert(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !busy) {
                                    e.preventDefault();
                                    onSpeichern();
                                }
                            }}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                appearance: "none",
                                border: 0,
                                outline: "none",
                                background: "transparent",
                                font: "inherit",
                                fontSize: REFINED.fs.md,
                                color: REFINED.ink,
                                padding: "9px 12px",
                            }}
                        />
                        {endung && (
                            <span
                                title="Die Endung bleibt — sie bestimmt, womit die Datei geöffnet wird."
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "0 12px",
                                    background: REFINED.fieldBg,
                                    borderLeft: `1px solid ${REFINED.line}`,
                                    color: REFINED.ink3,
                                    fontSize: REFINED.fs.sm,
                                    fontWeight: 600,
                                }}
                            >
                                {endung}
                            </span>
                        )}
                    </div>
                    {ort && <div style={{ fontSize: REFINED.fs.xs, color: REFINED.ink3 }}>{ort}</div>}
                    {hinweis && (
                        <div
                            role="status"
                            style={{
                                fontSize: REFINED.fs.sm,
                                color: REFINED.goldInk,
                                background: REFINED.goldBg,
                                border: `1px solid ${REFINED.goldLine}`,
                                borderRadius: 8,
                                padding: "8px 10px",
                                lineHeight: 1.45,
                            }}
                        >
                            {hinweis}
                        </div>
                    )}
                    {fehler && (
                        <div role="alert" style={{ fontSize: REFINED.fs.sm, color: REFINED.err }}>
                            {fehler}
                        </div>
                    )}
                </div>
            }
        />
    );
};

/**
 * Der Live-Hinweis für Schrägstriche im Dateinamen (T-456) — mit dem Namen,
 * der tatsächlich gespeichert wird.
 */
export const SchraegstrichHinweis: React.FC<{ ergebnis: string }> = ({ ergebnis }) => (
    <>
        „/“ und „\“ sind in Dateinamen nicht möglich und werden durch „-“ ersetzt. Gespeichert wird:{" "}
        <b style={{ wordBreak: "break-word" }}>{ergebnis}</b>
    </>
);

export default NamenEingabeDialog;
